import { useTranslation } from '@/core/hooks';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Platform } from 'react-native';
import { BLOOM_UNIVERSAL_HUB_FALLBACK } from '../constants/bloomUniversalContent.fallback';
import { fetchBloomUniversalHub } from '../services/bloomUniversalContentService';
import {
  findSpaceInHub,
  mapBloomUniversalHubFields,
} from '../services/mapBloomUniversalContent';
import {
  type BloomUniversalHubContent,
  type BloomUniversalSpaceContent,
} from '../types/bloomUniversalContent';

const HUB_STALE_MS = 5 * 60 * 1000;
/** Bloom Universal CMS screens are web-only (`GuestGate` + screen Platform checks). */
const IS_WEB = Platform.OS === 'web';

function useAppLocale(): 'en' | 'ar' {
  const { currentLanguage } = useTranslation();
  return currentLanguage === 'ar' ? 'ar' : 'en';
}

/**
 * Hub content — Contentful query by `content_type=bloomUniversalHub` (spaces nested).
 * Web only: no fetch / no fallback on native.
 * - Loading: no hub (UI stays blank)
 * - Success: CMS hub (or null if entry missing)
 * - Error: local fallback JSON only
 */
export function useBloomUniversalHubContent(): {
  readonly hub: BloomUniversalHubContent | null;
  readonly isLoading: boolean;
  readonly isFromFallback: boolean;
} {
  const locale = useAppLocale();
  const fallbackHub = useMemo(
    () => mapBloomUniversalHubFields(BLOOM_UNIVERSAL_HUB_FALLBACK),
    [],
  );

  const query = useQuery({
    queryKey: ['bloom-universal-hub', locale],
    queryFn: () => fetchBloomUniversalHub(locale),
    staleTime: HUB_STALE_MS,
    retry: 1,
    enabled: IS_WEB,
  });

  if (!IS_WEB) {
    return {
      hub: null,
      isLoading: false,
      isFromFallback: false,
    };
  }

  // v5: isLoading = pending && fetching (disabled queries stay idle, not loading)
  const isLoading = query.isLoading;
  const isFromFallback = query.isError;

  let hub: BloomUniversalHubContent | null = null;
  if (isFromFallback) {
    hub = fallbackHub;
  } else if (!isLoading) {
    hub = query.data ?? null;
  }

  return {
    hub,
    isLoading,
    isFromFallback,
  };
}

/**
 * Space detail — resolved from the hub payload (no separate space content-type query).
 * Web only. Fallback hub/space only when Contentful hub fetch fails.
 */
export function useBloomUniversalSpaceContent(spaceKey: string | undefined): {
  readonly hub: BloomUniversalHubContent | null;
  readonly space: BloomUniversalSpaceContent | undefined;
  readonly isLoading: boolean;
  readonly isFromFallback: boolean;
} {
  const {
    hub,
    isLoading,
    isFromFallback,
  } = useBloomUniversalHubContent();

  const trimmedKey = spaceKey?.trim() ?? '';

  const spaceFromHub = useMemo(
    () => (hub ? findSpaceInHub(hub, trimmedKey) : undefined),
    [hub, trimmedKey],
  );

  if (!IS_WEB) {
    return {
      hub: null,
      space: undefined,
      isLoading: false,
      isFromFallback: false,
    };
  }

  const space =
    spaceFromHub && spaceFromHub.isActive !== false
      ? spaceFromHub
      : undefined;

  return {
    hub,
    space,
    isLoading,
    isFromFallback,
  };
}
